import React, {useEffect} from 'react';
import {useRecoilState, useRecoilValue} from 'recoil';
import FeedbackDialog from '../../../../common/dialogs/FeedbackDialog';
import NonLoggedInUserDialog from '../../../../common/dialogs/NonLoggedInUserDialog';
import useBidmiiProTier from '../../../../common/useBidmiiProTier';
import {
  aboutReferralsDialogOpenState,
  bidmiiProUpgradeOpenState,
  createProjectDialogOpenState,
  directQuoteDialogOpenState,
  feedbackDialogOpenState,
  insuranceDialogOpenState,
  nonLoggedInUserDialogOpenState,
  restrictedContentWarningOpenState,
  supportPopupOpenState,
} from '../../../../recoil/dialogs/DialogsRecoil';
import {firestoreUserState, userState} from '../../../../recoil/user/userAtom';
import AboutReferralsDialog from '../../../BidmiiProSubscriptions/components/AboutReferralsDialog';
import RecurringUpgradePrompt from '../../../BidmiiProSubscriptions/components/RecurringUpgradePrompt';
import DirectQuoteDialog from '../../components/DirectQuoteDialog';
import moment from 'moment';
import RestrictedContentWarningDialog from '../../../../common/dialogs/RestrictedContentWarningDialog';
import {
  LazyInsuranceQualifications,
  LazyNewProjectWizard,
  LazySupportPopup,
} from '../../../../common/LazyLoader';
import {collection, doc, getDoc} from 'firebase/firestore';
import {firestoreDb} from '../../../../App';

interface IProps {}
export const Dialogs: React.FC<IProps> = (_props) => {
  const user = useRecoilValue(userState);
  const firestoreUser = useRecoilValue(firestoreUserState);
  const proTier = useBidmiiProTier();

  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useRecoilState(
    createProjectDialogOpenState,
  );
  const [directQuoteDialogOpen, setDirectQuoteDialogOpen] = useRecoilState(
    directQuoteDialogOpenState,
  );
  const [nonLoggedInUserDialogOpen, setNonLoggedInUserDialogOpen] =
    useRecoilState(nonLoggedInUserDialogOpenState);

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useRecoilState(
    feedbackDialogOpenState,
  );
  const [supportPopupOpen, setSupportPopupOpen] = useRecoilState(
    supportPopupOpenState,
  );
  const [upgradePromptOpen, setUpgradePromptOpen] = useRecoilState(
    bidmiiProUpgradeOpenState,
  );
  const [aboutReferralsOpen, setAboutReferralsOpen] = useRecoilState(
    aboutReferralsDialogOpenState,
  );
  const [insuranceDialogOpen, setInsuranceDialogOpen] = useRecoilState(
    insuranceDialogOpenState,
  );
  const [restrictedContentWarningOpen, setRestrictedContentWarningOpen] =
    useRecoilState(restrictedContentWarningOpenState);

  useEffect(() => {
    const promptDeadline = localStorage.getItem('bidmiiProUpgradeShown');
    if (
      user?.initialised &&
      user?.userType === 'BIDDER' &&
      user?.hasAgreedToTerms &&
      proTier === 0 &&
      !window.location.pathname.includes('onboarding') &&
      !window.location.pathname.includes('bidmii-pro') &&
      moment().isAfter(moment(user.createDate).add(2, 'days')) &&
      (!promptDeadline || moment().isAfter(moment(promptDeadline)))
    ) {
      setUpgradePromptOpen(true);
    }
  }, [proTier, setUpgradePromptOpen, user]);

  useEffect(() => {
    if (
      user?.userType === 'BIDDER' &&
      proTier > 0 &&
      !window.location.pathname.includes('new/user') &&
      !window.location.pathname.includes('onboarding') &&
      !window.location.pathname.includes('bidmii-pro') &&
      !sessionStorage.getItem('aboutReferralsDialogShown')
    ) {
      // @ts-ignore
      const userDocRef = doc(collection(firestoreDb, 'users'), user.uid);
      getDoc(userDocRef).then((doc) => {
        if (
          !doc.exists() ||
          (doc.exists() && !doc.data()?.aboutReferralsDialogShown)
        ) {
          setAboutReferralsOpen(2);
        }
      });
    }
  }, [proTier, setAboutReferralsOpen, user]);

  useEffect(() => {
    if (
      user?.initialised &&
      user?.userType === 'BIDDER' &&
      user?.hasAgreedToTerms &&
      !user?.insured
    ) {
      if (firestoreUser && !firestoreUser?.insuranceQualificationsShown) {
        setInsuranceDialogOpen(true);
      }
    }
  }, [setInsuranceDialogOpen, user, firestoreUser]);

  return (
    <>
      {createProjectDialogOpen ? (
        <LazyNewProjectWizard
          open={createProjectDialogOpen}
          handleClose={() => setCreateProjectDialogOpen(false)}
        />
      ) : null}

      {directQuoteDialogOpen && (
        <DirectQuoteDialog
          open={directQuoteDialogOpen}
          toggleDirectQuoteDialog={() =>
            setDirectQuoteDialogOpen(!directQuoteDialogOpen)
          }
        />
      )}

      <NonLoggedInUserDialog
        open={nonLoggedInUserDialogOpen}
        handleClose={() => setNonLoggedInUserDialogOpen(false)}
      />

      {feedbackDialogOpen ? (
        <FeedbackDialog handleClose={() => setFeedbackDialogOpen(false)} />
      ) : null}

      {supportPopupOpen ? (
        <LazySupportPopup
          open={supportPopupOpen}
          handleClose={() => setSupportPopupOpen(false)}
        />
      ) : null}

      {upgradePromptOpen ? (
        <RecurringUpgradePrompt
          handleClose={() => setUpgradePromptOpen(false)}
        />
      ) : null}

      {aboutReferralsOpen ? (
        <AboutReferralsDialog
          handleClose={() => {
            setAboutReferralsOpen(0);
          }}
        />
      ) : null}

      {insuranceDialogOpen ? (
        <LazyInsuranceQualifications
          handleClose={() => setInsuranceDialogOpen(false)}
        />
      ) : null}

      <RestrictedContentWarningDialog
        open={restrictedContentWarningOpen}
        handleClose={() => setRestrictedContentWarningOpen(false)}
      />
    </>
  );
};
